/**
 * Kitsters AI Recommender - frontend interaction layer.
 *
 * Architecture mirrors the blog agent (scripts/blog_agent/writer.py):
 *   ANTHROPIC_API_KEY lives server-side only (netlify/functions/ai-chat.js).
 *   Browser POSTs conversation history → proxy → Claude API → structured JSON.
 *   Product data fetched from Shopify Storefront API using filters Claude extracts.
 */

(function () {
  'use strict';

  /* ── Shopify Storefront API (public, no secret needed) ──────────────────── */
  const STOREFRONT_API_ENDPOINT = '/api/2024-01/graphql.json';

  /* ── Config ─────────────────────────────────────────────────────────────── */
  const configEl  = document.getElementById('karConfig');
  if (!configEl) return;
  const CONFIG    = JSON.parse(configEl.textContent);

  /* ── Claude proxy endpoint - set via theme editor (section settings) ─────
     Empty = no Netlify URL configured yet; falls back to built-in responses.  */
  const KAR_API_ENDPOINT = CONFIG.aiEndpointUrl || '';

  /* ── DOM refs ───────────────────────────────────────────────────────────── */
  const messagesEl    = document.getElementById('karMessages');
  const typingEl      = document.getElementById('karTyping');
  const chipsEl       = document.getElementById('karChips');
  const inputEl       = document.getElementById('karInput');
  const formEl        = document.getElementById('karInputForm');
  const startOverBtn  = document.getElementById('karStartOver');
  const productGrid   = document.getElementById('karProductGrid');
  const panelEmpty    = document.getElementById('karPanelEmpty');

  /* ── Session state ──────────────────────────────────────────────────────── */
  const state = {
    sessionId:    generateSessionId(),
    history:      [],            // [{role, content}]
    nudgeShown:   false,
    cartCount:    0,
  };

  /* ── Init ───────────────────────────────────────────────────────────────── */
  function init() {
    fireEvent('session_start', { sessionId: state.sessionId });
    renderWelcome();
    bindEvents();
    injectToast();
  }

  /* ── Events ─────────────────────────────────────────────────────────────── */
  function bindEvents() {
    formEl.addEventListener('submit', handleSubmit);
    startOverBtn.addEventListener('click', resetSession);
    /* delegate ATC clicks from both panels */
    messagesEl.addEventListener('click', handleDelegatedClick);
    if (productGrid) {
      productGrid.addEventListener('click', handleDelegatedClick);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    const text = inputEl.value.trim();
    if (!text) return;
    sendMessage(text);
  }

  function handleDelegatedClick(e) {
    /* ATC button */
    const atcBtn = e.target.closest('[data-variant-id].kar-card__atc-btn, [data-variant-id].kar-nudge-card__add-btn');
    if (atcBtn) {
      addToCart(atcBtn);
      return;
    }
    /* Carousel nav */
    const carouselBtn = e.target.closest('.kar__carousel-btn');
    if (carouselBtn) {
      const dir      = carouselBtn.dataset.dir;
      const carousel = carouselBtn.closest('.kar__carousel-wrap').querySelector('.kar__carousel');
      scrollCarousel(carousel, dir);
      return;
    }
  }

  /* ── Welcome ─────────────────────────────────────────────────────────────── */
  function renderWelcome() {
    const chips = CONFIG.welcomeChips || ['For myself', 'A gift', 'Corporate/Bulk'];
    appendAIMessage(CONFIG.welcomeMessage, chips);
  }

  /* ── Core message flow ───────────────────────────────────────────────────── */
  function sendMessage(text) {
    clearChips();
    appendUserMessage(text);
    inputEl.value = '';
    fireEvent('message_sent', { text, sessionId: state.sessionId });

    state.history.push({ role: 'user', content: text });

    showTyping();
    requestAIResponse();
  }

  /**
   * requestAIResponse - calls the server-side Claude proxy (netlify/functions/ai-chat.js).
   * Mirrors the blog agent's architecture: ANTHROPIC_API_KEY lives only on the server;
   * the browser never sees it. The proxy returns {message, chips, intent, filters}.
   *
   * When intent === "recommend", filters are used to query the Shopify Storefront API
   * for live product results (fetchProductsByFilters). All other intents show the AI
   * message + chips only.
   */
  function requestAIResponse() {
    /* No endpoint configured yet - use built-in guided responses */
    if (!KAR_API_ENDPOINT) {
      const lastUserMsg = state.history.filter(function (m) { return m.role === 'user'; }).pop();
      const response    = buildStubResponse(lastUserMsg ? lastUserMsg.content : '');
      setTimeout(function () {
        hideTyping();
        state.history.push({ role: 'assistant', content: response.message });
        if (response.products && response.products.length) {
          appendAIMessage(response.message, response.chips || [], response.products);
          renderProductPanel(response.products);
        } else {
          appendAIMessage(response.message, response.chips || []);
        }
      }, 800 + Math.random() * 400);
      return;
    }

    fetch(KAR_API_ENDPOINT, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        history:   state.history,
        sessionId: state.sessionId,
      }),
    })
    .then(function (res) {
      if (!res.ok) throw new Error('API ' + res.status);
      return res.json();
    })
    .then(function (data) {
      hideTyping();

      /* Push assistant turn into history so the next call has full context */
      state.history.push({ role: 'assistant', content: data.message });

      if (data.intent === 'recommend' && data.filters) {
        /* Fetch real products from Shopify Storefront API, then render */
        fetchProductsByFilters(data.filters).then(function (products) {
          if (products.length) {
            appendAIMessage(data.message, data.chips || [], products);
            renderProductPanel(products);
            fireEvent('recommendation_shown', {
              sessionId: state.sessionId,
              count:     products.length,
              filters:   data.filters,
            });
          } else {
            appendAIMessage(
              data.message + ' (I\'m still learning the catalog - let me know if you\'d like different options!)',
              data.chips || []
            );
          }
        });
      } else if (data.intent === 'bulk_inquiry') {
        const bulkMsg = data.message +
          (data.bulk_note ? '\n\n' + data.bulk_note : '');
        appendAIMessage(bulkMsg, data.chips || []);
      } else {
        appendAIMessage(data.message, data.chips || []);
      }
    })
    .catch(function (err) {
      hideTyping();
      console.error('[KAR] AI fetch error:', err);
      appendAIMessage(
        'Sorry, I had trouble connecting right now. Please try again in a moment!',
        ['Try again', 'Browse all kits']
      );
    });
  }

  /* ── Render helpers ──────────────────────────────────────────────────────── */
  function appendUserMessage(text) {
    const row = document.createElement('div');
    row.className = 'kar__bubble-row kar__bubble-row--user';
    row.innerHTML = '<div class="kar__bubble kar__bubble--user">' + escHtml(text) + '</div>';
    messagesEl.appendChild(row);
    scrollToBottom();
  }

  function appendAIMessage(text, chips, products) {
    const row = document.createElement('div');
    row.className = 'kar__bubble-row kar__bubble-row--ai';

    const avatarClone = buildAvatarEl('kar__avatar--sm');
    const bubble      = document.createElement('div');
    bubble.className  = 'kar__bubble kar__bubble--ai';
    bubble.innerHTML  = escHtml(text);

    if (products && products.length) {
      bubble.appendChild(buildCarousel(products));
    }

    row.appendChild(avatarClone);
    row.appendChild(bubble);
    messagesEl.appendChild(row);

    if (chips && chips.length) {
      renderChips(chips);
    } else {
      clearChips();
    }

    scrollToBottom();
  }

  function appendAINudge(primaryProduct, nudgeProducts) {
    if (!nudgeProducts || !nudgeProducts.length) return;
    const row    = document.createElement('div');
    row.className = 'kar__bubble-row kar__bubble-row--ai';

    const avatar  = buildAvatarEl('kar__avatar--sm');
    const bubble  = document.createElement('div');
    bubble.className = 'kar__bubble kar__bubble--ai';

    const maxNudgePrice = Math.max(...nudgeProducts.map(function (p) { return p.rawPrice; }));
    const formattedMax  = formatPrice(maxNudgePrice);

    bubble.innerHTML =
      '<strong>' + escHtml(primaryProduct.name) +
      ' (' + escHtml(primaryProduct.price) + ')</strong> is in your cart! ' +
      'Many customers also grab one of these to get the most out of it - ' +
      'and they\'re all under <strong>' + escHtml(formattedMax) + '</strong>:';

    const nudgeWrap  = document.createElement('div');
    nudgeWrap.className = 'kar__nudge-wrap';

    const nudgeLabel = document.createElement('div');
    nudgeLabel.className = 'kar__nudge-label';
    nudgeLabel.textContent = 'Pairs well with your kit ✨';
    nudgeWrap.appendChild(nudgeLabel);

    const strip = document.createElement('div');
    strip.className = 'kar__nudge-strip';
    nudgeProducts.forEach(function (p) {
      strip.appendChild(buildNudgeCard(p));
    });
    nudgeWrap.appendChild(strip);
    bubble.appendChild(nudgeWrap);

    /* Nudge chips */
    const nudgeChips = nudgeProducts.map(function (p) {
      return 'Add ' + truncate(p.name, 22) + ' - ' + p.price;
    }).concat(['No thanks, checkout']);

    row.appendChild(avatar);
    row.appendChild(bubble);
    messagesEl.appendChild(row);
    renderChips(nudgeChips);
    scrollToBottom();

    fireEvent('aov_nudge_triggered', { sessionId: state.sessionId });
    state.nudgeShown = true;
  }

  /* ── Carousel builder ──────────────────────────────────────────────────── */
  function buildCarousel(products) {
    const wrap = document.createElement('div');
    wrap.className = 'kar__carousel-wrap';

    const carousel = document.createElement('div');
    carousel.className = 'kar__carousel';

    products.slice(0, 5).forEach(function (p) {
      carousel.appendChild(buildProductCard(p));
    });
    wrap.appendChild(carousel);

    /* Nav arrows - only if more than 2 products */
    if (products.length > 2) {
      const nav = document.createElement('div');
      nav.className = 'kar__carousel-nav';
      nav.innerHTML =
        '<button class="kar__carousel-btn" type="button" data-dir="prev" aria-label="Previous product">◀</button>' +
        '<button class="kar__carousel-btn" type="button" data-dir="next" aria-label="Next product">▶</button>';
      wrap.appendChild(nav);
    }

    if (products.length > 5) {
      const seeMore = document.createElement('a');
      seeMore.className = 'kar__see-more';
      seeMore.href = '/collections/all';
      seeMore.textContent = 'See all results →';
      wrap.appendChild(seeMore);
    }

    return wrap;
  }

  /* ── Product card (DOM, not Liquid render - for JS-only insertion) ────── */
  function buildProductCard(p) {
    const card = document.createElement('article');
    card.className = 'kar-card';
    card.dataset.handle = p.handle || '';
    card.dataset.variantId = p.variantId || '';

    const stockBadge = !p.inStock
      ? '<span class="kar-card__stock kar-card__stock--oos">Out of Stock</span>'
      : (p.lowStockQty > 0
          ? '<span class="kar-card__stock kar-card__stock--low">Only ' + p.lowStockQty + ' left</span>'
          : '');

    const badgeHtml = p.badge
      ? '<span class="kar-card__badge kar-card__badge--' + escHtml(p.badgeType || 'default') + '">' + escHtml(p.badge) + '</span>'
      : '';

    const imgHtml = p.image
      ? '<img src="' + escHtml(p.image) + '" alt="' + escHtml(p.imageAlt || p.name) + '" width="300" height="300" loading="lazy" class="kar-card__img">'
      : '<div class="kar-card__img-placeholder" aria-hidden="true"></div>';

    const ratingHtml = p.rating
      ? '<div class="kar-card__rating" aria-label="' + p.rating + ' out of 5 stars">' +
          '<span class="kar-card__stars" aria-hidden="true">' + buildStars(p.rating) + '</span>' +
          (p.ratingCount ? '<span class="kar-card__rating-count">(' + p.ratingCount + ')</span>' : '') +
        '</div>'
      : '';

    const compareHtml = p.comparePrice
      ? '<s class="kar-card__compare">' + escHtml(p.comparePrice) + '</s>'
      : '';

    const atcHtml = p.inStock
      ? '<button type="button" class="kar-card__atc-btn" data-variant-id="' + escHtml(String(p.variantId)) + '" data-product-name="' + escHtml(p.name) + '" data-product-price="' + escHtml(p.price) + '" aria-label="Add ' + escHtml(p.name) + ' to cart">Add to Cart</button>'
      : '';

    card.innerHTML =
      '<a href="/products/' + escHtml(p.handle) + '" class="kar-card__image-link" tabindex="-1" aria-hidden="true">' +
        '<div class="kar-card__image-wrap">' +
          imgHtml + badgeHtml + stockBadge +
        '</div>' +
      '</a>' +
      '<div class="kar-card__body">' +
        '<a href="/products/' + escHtml(p.handle) + '" class="kar-card__name-link"><h3 class="kar-card__name">' + escHtml(p.name) + '</h3></a>' +
        ratingHtml +
        '<div class="kar-card__price-row"><span class="kar-card__price">' + escHtml(p.price) + '</span>' + compareHtml + '</div>' +
        '<div class="kar-card__actions"><a href="/products/' + escHtml(p.handle) + '" class="kar-card__view-btn">View Details</a>' + atcHtml + '</div>' +
      '</div>';

    return card;
  }

  function buildNudgeCard(p) {
    const card = document.createElement('article');
    card.className = 'kar-nudge-card';
    card.dataset.handle = p.handle || '';

    const imgHtml = p.image
      ? '<img src="' + escHtml(p.image) + '" alt="' + escHtml(p.imageAlt || p.name) + '" width="80" height="80" loading="lazy" class="kar-nudge-card__img">'
      : '<div class="kar-nudge-card__img-placeholder" aria-hidden="true"></div>';

    card.innerHTML =
      '<div class="kar-nudge-card__image-wrap">' + imgHtml + '</div>' +
      '<div class="kar-nudge-card__info">' +
        '<p class="kar-nudge-card__name">' + escHtml(p.name) + '</p>' +
        '<span class="kar-nudge-card__price">' + escHtml(p.price) + '</span>' +
      '</div>' +
      '<button type="button" class="kar-nudge-card__add-btn" data-variant-id="' + escHtml(String(p.variantId)) + '" data-product-name="' + escHtml(p.name) + '" data-product-price="' + escHtml(p.price) + '" aria-label="Add ' + escHtml(p.name) + ' to cart">Add</button>';

    return card;
  }

  function buildAvatarEl(extraClass) {
    const el = document.createElement('div');
    el.className = 'kar__avatar' + (extraClass ? ' ' + extraClass : '');
    el.setAttribute('aria-hidden', 'true');
    /* Inline SVG mascot - matches ai-rec-avatar.liquid */
    el.innerHTML =
      '<svg class="kar__avatar-svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">' +
        '<circle cx="16" cy="14" r="10" fill="#fdda0d" stroke="#0a0a0a" stroke-width="1.8"/>' +
        '<circle cx="12.5" cy="13" r="1.8" fill="#0a0a0a"/>' +
        '<circle cx="19.5" cy="13" r="1.8" fill="#0a0a0a"/>' +
        '<circle cx="13.2" cy="12.2" r="0.6" fill="#fff"/>' +
        '<circle cx="20.2" cy="12.2" r="0.6" fill="#fff"/>' +
        '<path d="M12 17c1 1.8 7 1.8 8 0" stroke="#0a0a0a" stroke-width="1.5" stroke-linecap="round"/>' +
        '<path d="M16 4L10 14h12L16 4z" fill="#465ae6" stroke="#0a0a0a" stroke-width="1.5" stroke-linejoin="round"/>' +
        '<circle cx="16" cy="4" r="1.5" fill="#ff4683" stroke="#0a0a0a" stroke-width="1.2"/>' +
        '<rect x="11" y="23" width="10" height="6" rx="3" fill="#465ae6" stroke="#0a0a0a" stroke-width="1.5"/>' +
      '</svg>';
    return el;
  }

  /* ── Product panel (desktop sidebar) ─────────────────────────────────────── */
  function renderProductPanel(products) {
    if (!productGrid) return;
    productGrid.innerHTML = '';
    if (panelEmpty) panelEmpty.style.display = 'none';

    products.slice(0, 6).forEach(function (p) {
      productGrid.appendChild(buildProductCard(p));
    });
    productGrid.style.display = '';

    productGrid.addEventListener('click', function onGridClick(e) {
      const btn = e.target.closest('.kar-card__atc-btn');
      if (btn) addToCart(btn);
    }, { once: false });
  }

  /* ── Chips ───────────────────────────────────────────────────────────────── */
  function renderChips(chips) {
    clearChips();
    chips.forEach(function (label) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kar__chip';
      btn.setAttribute('role', 'listitem');
      btn.textContent = label;
      btn.addEventListener('click', function () {
        /* 'No thanks, checkout' routes to cart */
        if (label === 'No thanks, checkout') {
          fireEvent('aov_nudge_declined', { sessionId: state.sessionId });
          window.location.href = '/cart';
          return;
        }
        /* Nudge add chips.

           The price half of this pattern used to be anchored on `₹`, which made
           it a hidden currency dependency: the chip label is BUILT by formatting
           a price (line ~257) and then re-PARSED here, so the moment the shop's
           money_format stops starting with a rupee sign the match silently fails
           and the `aov_nudge_accepted` event stops firing — no error, and the
           chip still works, so nothing looks broken except a metric quietly
           going to zero.

           It is currency-agnostic now. Group 1 stays greedy so the split lands
           on the LAST " - " in the label, which is the separator this string is
           built with; a product name containing " - " therefore still parses. */
        const nudgeAddMatch = label.match(/^Add (.+) - (.+)$/);
        if (nudgeAddMatch) {
          fireEvent('aov_nudge_accepted', { sessionId: state.sessionId, item: nudgeAddMatch[1] });
        }
        sendMessage(label);
      });
      chipsEl.appendChild(btn);
    });
  }

  function clearChips() {
    chipsEl.innerHTML = '';
  }

  /* ── Typing indicator ────────────────────────────────────────────────────── */
  function showTyping() {
    typingEl.setAttribute('aria-hidden', 'false');
    scrollToBottom();
  }

  function hideTyping() {
    typingEl.setAttribute('aria-hidden', 'true');
  }

  /* ── Add to Cart ─────────────────────────────────────────────────────────── */
  function addToCart(btn) {
    const variantId   = btn.dataset.variantId;
    const productName = btn.dataset.productName;
    const productPrice= btn.dataset.productPrice;
    if (!variantId) return;

    btn.classList.add('is-loading');
    const originalText = btn.textContent;
    btn.textContent = '…';

    fetch(CONFIG.cartApiUrl, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity: 1 }),
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (data.status && data.status !== 200) {
        throw new Error(data.description || 'Could not add to cart');
      }
      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      btn.textContent = '✓ Added';

      updateCartBadge();
      showToast('✓ ' + productName + ' added to cart!');
      fireEvent('add_to_cart_from_ai', {
        sessionId:   state.sessionId,
        variantId:   variantId,
        productName: productName,
      });

      /* AOV nudge - triggered only on primary ATC (not nudge card) */
      if (!state.nudgeShown && btn.classList.contains('kar-card__atc-btn')) {
        state.nudgeShown = false; /* will be set inside appendAINudge */
        triggerNudgeFlow({ name: productName, price: productPrice });
      }
    })
    .catch(function (err) {
      btn.classList.remove('is-loading');
      btn.textContent = originalText;
      showToast('Oops - could not add to cart. Try again?');
      console.error('[KAR] ATC error:', err);
    });
  }

  /* ── AOV nudge flow ────────────────────────────────────────────────────── */
  function triggerNudgeFlow(primaryProduct) {
    showTyping();
    getStubNudgeCandidates(primaryProduct).then(function (nudgeCandidates) {
      hideTyping();
      if (nudgeCandidates.length) {
        appendAIMessage(
          'Done! I\'ve added ' + primaryProduct.name + ' to your cart 🎉 Want to keep shopping or head to checkout?',
          []
        );
        appendAINudge(primaryProduct, nudgeCandidates);
      } else {
        appendAIMessage(
          'Done! I\'ve added ' + primaryProduct.name + ' to your cart 🎉 Want to keep shopping or head to checkout?',
          ['Keep shopping', 'Go to checkout']
        );
      }
    });
  }

  /* ── Cart badge update ───────────────────────────────────────────────────── */
  function updateCartBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const badges = document.querySelectorAll('[data-cart-count], .cart-count, .header__cart-count');
        badges.forEach(function (el) {
          el.textContent = cart.item_count;
          el.setAttribute('aria-label', cart.item_count + ' items in cart');
        });
      })
      .catch(function () {});
  }

  /* ── Toast ────────────────────────────────────────────────────────────────── */
  function injectToast() {
    const toast = document.createElement('div');
    toast.id = 'karToast';
    toast.className = 'kar__toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  function showToast(msg) {
    const toast = document.getElementById('karToast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3000);
  }

  /* ── Carousel scroll ────────────────────────────────────────────────────── */
  function scrollCarousel(carousel, dir) {
    const cardWidth = carousel.querySelector('.kar-card')
      ? carousel.querySelector('.kar-card').offsetWidth + 12
      : 232;
    carousel.scrollBy({ left: dir === 'next' ? cardWidth : -cardWidth, behavior: 'smooth' });
  }

  /* ── Helpers ─────────────────────────────────────────────────────────────── */
  function scrollToBottom() {
    requestAnimationFrame(function () {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function truncate(str, max) {
    return str.length > max ? str.slice(0, max - 1) + '…' : str;
  }

  /* Storefront API returns `variant.price.amount` as a decimal string in MAJOR
     units, so this is formatMajor and not format. The old parameter name said
     `paise` and was wrong about its own input the whole time. */
  function formatPrice(amount) {
    return window.KitstersMoney.formatMajor(amount);
  }

  function buildStars(rating) {
    const full = Math.floor(Number(rating));
    let html = '';
    for (let i = 1; i <= 5; i++) {
      html += i <= full
        ? '<span class="kar-card__star kar-card__star--full">★</span>'
        : '<span class="kar-card__star">☆</span>';
    }
    return html;
  }

  function generateSessionId() {
    return 'kar-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
  }

  function fireEvent(name, payload) {
    /* Phase 1: console log. Backend will replace with real analytics sink. */
    if (window.KAR_DEBUG) console.log('[KAR event]', name, payload);
    /* Push to dataLayer for GA4 if present */
    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: 'kar_' + name }, payload));
    }
  }

  /* ── Session reset ────────────────────────────────────────────────────────── */
  function resetSession() {
    state.history   = [];
    state.nudgeShown = false;
    messagesEl.innerHTML = '';
    clearChips();
    if (productGrid) {
      productGrid.innerHTML = '';
      productGrid.style.display = 'none';
    }
    if (panelEmpty) panelEmpty.style.display = '';
    state.sessionId = generateSessionId();
    fireEvent('session_start', { sessionId: state.sessionId });
    renderWelcome();
  }

  /* ── Shopify Storefront API - live product search ────────────────────────
     Called when Claude returns intent="recommend" with filters.
     Uses the public Storefront API (no secret required in the browser).
     Requires a Shopify Storefront API public access token set in the section
     settings or as a meta tag - see STOREFRONT_TOKEN below.
     ──────────────────────────────────────────────────────────────────────── */

  /* Storefront public token - injected by the section via a <meta> tag.
     The section sets: <meta name="kar-storefront-token" content="{{ shop.metafields.kar.storefront_token }}">
     For now we fall back gracefully if not present. */
  const STOREFRONT_TOKEN = (function () {
    const meta = document.querySelector('meta[name="kar-storefront-token"]');
    return meta ? meta.getAttribute('content') : '';
  })();

  function fetchProductsByFilters(filters) {
    /* Build a Shopify product search query from the filters Claude extracted */
    const queryParts = ['available_for_sale:true'];

    if (filters.budget_max) {
      queryParts.push('price:<=' + filters.budget_max);
    }

    /* Map age/skill/mess filters to Shopify product tags */
    if (filters.age)   queryParts.push('tag:' + filters.age);
    if (filters.skill) queryParts.push('tag:' + filters.skill);
    if (filters.mess)  queryParts.push('tag:' + filters.mess);

    /* Restricted vendor (docs/19 MH-VIS). Magic Hour kits are ACTIVE so Razorpay
       Magic Checkout accepts them, and the Storefront API returns them like any
       other product. This query runs in the browser, so Liquid cannot filter it -
       the vendor comes from the <meta> theme.liquid writes from the same theme
       setting every server-side surface reads. Negating in the query is the
       mechanism; the .filter() below is the net for a query syntax change. */
    const hiddenVendor = (function () {
      const m = document.querySelector('meta[name="kt-hidden-vendor"]');
      return m ? m.getAttribute('content') : '';
    })();
    if (hiddenVendor) queryParts.push('-vendor:' + JSON.stringify(hiddenVendor));

    const searchQuery = queryParts.join(' ');

    const gql = `{
      products(first: 5, query: ${JSON.stringify(searchQuery)}, sortKey: BEST_SELLING) {
        edges {
          node {
            title
            handle
            vendor
            tags
            priceRange {
              minVariantPrice { amount }
            }
            compareAtPriceRange {
              minVariantPrice { amount }
            }
            images(first: 1) {
              edges { node { url altText width height } }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  availableForSale
                  quantityAvailable
                  price { amount }
                  compareAtPrice { amount }
                }
              }
            }
            metafield(namespace: "reviews", key: "rating") { value }
            ratingCount: metafield(namespace: "reviews", key: "rating_count") { value }
          }
        }
      }
    }`;

    if (!STOREFRONT_TOKEN) {
      /* No token configured yet - return empty so chat degrades gracefully */
      console.warn('[KAR] No Storefront API token found. Set shop.metafields.kar.storefront_token.');
      return Promise.resolve([]);
    }

    return fetch(STOREFRONT_API_ENDPOINT, {
      method:  'POST',
      headers: {
        'Content-Type':                      'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: gql }),
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (!data.data || !data.data.products) return [];
      return data.data.products.edges
        .filter(function (edge) {
          return !hiddenVendor || edge.node.vendor !== hiddenVendor;
        })
        .map(function (edge) {
          return normaliseStorefrontProduct(edge.node);
        });
    })
    .catch(function (err) {
      console.error('[KAR] Storefront API error:', err);
      return [];
    });
  }

  function normaliseStorefrontProduct(node) {
    const variant    = node.variants.edges[0] && node.variants.edges[0].node;
    const imageNode  = node.images.edges[0] && node.images.edges[0].node;
    const rawPrice   = variant ? parseFloat(variant.price.amount) : 0;
    const rawCompare = variant && variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : 0;
    const qty        = variant ? (variant.quantityAvailable || 0) : 0;

    /* Extract badge from tags */
    const tags      = node.tags || [];
    const isBest    = tags.includes('bestseller');
    const ageTag    = tags.find(function (t) { return t.startsWith('ages'); });
    const skillTag  = tags.find(function (t) { return ['beginner','intermediate','advanced'].includes(t); });
    const badge     = isBest ? 'Bestseller' : (ageTag ? ageTag.replace('ages-', 'Ages ') : (skillTag ? capitalise(skillTag) : ''));
    const badgeType = isBest ? 'bestseller' : (ageTag ? 'agerange' : 'skill');

    return {
      name:        node.title,
      handle:      node.handle,
      price:       formatPrice(rawPrice),
      comparePrice: rawCompare > rawPrice ? formatPrice(rawCompare) : '',
      image:       imageNode ? imageNode.url : '',
      imageAlt:    imageNode && imageNode.altText ? imageNode.altText : node.title,
      badge:       badge,
      badgeType:   badgeType,
      rating:      node.metafield ? parseFloat(node.metafield.value) : null,
      ratingCount: node.ratingCount ? node.ratingCount.value : null,
      variantId:   variant ? variant.id.replace('gid://shopify/ProductVariant/', '') : '',
      inStock:     variant ? variant.availableForSale : false,
      lowStockQty: qty > 0 && qty < 5 ? qty : 0,
      rawPrice:    rawPrice,
    };
  }

  function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  /* AOV nudge candidates - fetches global add-on products tagged "add_on" */
  function getStubNudgeCandidates(primaryProduct) {
    if (!STOREFRONT_TOKEN) return Promise.resolve([]);

    const maxPrice = primaryProduct.rawPrice ? primaryProduct.rawPrice * 0.35 : 500;
    const gql = `{
      products(first: 3, query: "tag:add_on available_for_sale:true price:<=${maxPrice}", sortKey: BEST_SELLING) {
        edges {
          node {
            title handle
            images(first: 1) { edges { node { url altText } } }
            variants(first: 1) {
              edges { node { id price { amount } availableForSale } }
            }
          }
        }
      }
    }`;

    return fetch(STOREFRONT_API_ENDPOINT, {
      method:  'POST',
      headers: {
        'Content-Type':                      'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: gql }),
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      if (!data.data || !data.data.products) return [];
      return data.data.products.edges.map(function (edge) {
        const n = edge.node;
        const v = n.variants.edges[0] && n.variants.edges[0].node;
        const img = n.images.edges[0] && n.images.edges[0].node;
        const raw = v ? parseFloat(v.price.amount) : 0;
        return {
          name:     n.title,
          handle:   n.handle,
          price:    formatPrice(raw),
          rawPrice: raw,
          image:    img ? img.url : '',
          imageAlt: img && img.altText ? img.altText : n.title,
          variantId: v ? v.id.replace('gid://shopify/ProductVariant/', '') : '',
        };
      });
    })
    .catch(function () { return []; });
  }

  /* ── Built-in guided responses (fallback when no AI endpoint configured) ──
     Active until owner sets the Netlify URL in theme editor section settings.
     ──────────────────────────────────────────────────────────────────────── */

  function buildStubResponse(userText) {
    const lower = userText.toLowerCase();

    if (lower.includes('corporate') || lower.includes('bulk') || lower.includes('team')) {
      return {
        message: "For corporate and bulk orders, I'd love to help! Could you tell me: (1) how many kits you need, (2) your per-person budget, and (3) the event date? I'll connect you with our team for invoicing + logistics.",
        chips: ['Under $25/person', '$25-$50/person', 'Above $50/person'],
        products: [],
      };
    }
    if (lower.includes('gift') || lower.includes('birthday') || lower.includes('niece') || lower.includes('nephew')) {
      return {
        message: "Lovely - I'd love to help you find the perfect gift! How old is the lucky person?",
        chips: ['Under 6 years', '6-9 years', '10-14 years', '15+ / Adult'],
        products: [],
      };
    }
    if (lower.match(/\b(6|7|8|9|10|11|12|13|14)\b.*year/) || lower.includes('6-9') || lower.includes('10-14') || lower.includes('years old')) {
      return {
        message: "Great! What's your rough budget for the kit?",
        chips: ['Under $25', '$25-$50', '$50-$100', 'No limit'],
        products: [],
      };
    }
    if (lower.includes('adult') || lower.includes('myself') || lower.includes('me') || lower.includes('hobby')) {
      return {
        message: "Fun! What kind of experience are you after?",
        chips: ['Something creative', 'Low-mess', 'Food kit', 'Beginner-friendly'],
        products: [],
      };
    }
    if (lower.includes('$') || lower.includes('under') || lower.includes('budget') || lower.includes('25') || lower.includes('50') || lower.includes('100')) {
      return {
        message: "Perfect - that helps a lot! Head to our full catalog to see kits in that range, or tell me more and I'll narrow it down further.",
        chips: ['Show me food kits', 'Show me art kits', 'Beginner options', 'Browse all kits'],
        products: getStubProducts(),
      };
    }
    if (lower.includes('for myself') || lower.includes('a gift')) {
      return {
        message: "Got it! How old is the person this is for?",
        chips: ['Under 6 years', '6-9 years', '10-14 years', '15+ / Adult'],
        products: [],
      };
    }
    return {
      message: "I'd love to help! Is this kit for yourself or as a gift for someone?",
      chips: ['For myself', 'A gift', 'Corporate/Bulk'],
      products: [],
    };
  }

  function getStubProducts() {
    return [
      {
        name: 'Sushi Making Kit', handle: 'sushi-making-kit',
        price: '$32.00', comparePrice: '$39.00',
        image: '', imageAlt: 'Sushi Making Kit',
        badge: 'Bestseller', badgeType: 'bestseller',
        rating: 4.8, ratingCount: 312,
        variantId: '', inStock: true, lowStockQty: 0,
      },
      {
        name: 'Cotton Candy Machine Kit', handle: 'cotton-candy-machine-kit',
        price: '$24.00', comparePrice: '',
        image: '', imageAlt: 'Cotton Candy Machine Kit',
        badge: 'Ages 6-12', badgeType: 'agerange',
        rating: 4.6, ratingCount: 204,
        variantId: '', inStock: true, lowStockQty: 3,
      },
      {
        name: 'Resin Art Starter Kit', handle: 'resin-art-starter-kit',
        price: '$44.00', comparePrice: '',
        image: '', imageAlt: 'Resin Art Starter Kit',
        badge: 'Beginner', badgeType: 'skill',
        rating: 4.7, ratingCount: 178,
        variantId: '', inStock: true, lowStockQty: 0,
      },
    ];
  }

  /* ── Boot ────────────────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
